import {AuthType, ErrorCode, headerNames} from '../constants';
import {GoogleAuth, GoogleUser} from '../core/google-api';
import {SnapAuth} from '../core/snap-auth';
import {LogService} from '../shared/log';
import {SignInControl, SignUpControl, UiService} from '../shared/ui';

export class GoogleComponent {
  private readonly formName = 'alt';

  constructor(
    private readonly googleAuth: GoogleAuth,
    private readonly snapAuth: SnapAuth,
    private readonly signInControl: SignInControl,
    private readonly signUpControl: SignUpControl,
    private readonly ui: UiService,
    private readonly log: LogService,
  ) {
    this.signInControl.hidden = false;
    this.signUpControl.hidden = true;

    // Already signed in
    if (this.googleAuth.isSignedIn.get()) {
      this.log.warn('Already signed in from Google... Signing out');
      this.googleAuth.signOut();
    }

    this.handleSignInInteraction = this.handleSignInInteraction.bind(this);
    this.handleSignUpInteraction = this.handleSignUpInteraction.bind(this);

    // Listen for click on sign in button
    this.signInControl.registerInteractionHandler('click', this.handleSignInInteraction);

    // Listen for click on sign up button
    this.signUpControl.registerInteractionHandler('click', this.handleSignUpInteraction);
  }

  private async handleSignInInteraction() {
    this.googleAuth.signIn().then(this.signIn.bind(this), this.handleGooglePopupError.bind(this));
  }

  private async handleSignUpInteraction() {
    if (this.googleAuth.isSignedIn.get()) {
      await this.signUp(this.googleAuth.currentUser.get());
    }
  }

  private handleGooglePopupError(response: any) {
    if (response == null || typeof response !== 'object') {
      return;
    }

    if (response.error === 'popup_blocked_by_browser') {
      this.setHelptext(undefined, new Map([['google', 'Popup blocked by browser']]));
    }
  }

  private navigate(response: Response) {
    const redirectUrl = response.headers.get(headerNames.REDIRECT_URL);
    window.location.href = redirectUrl || '/';
  }

  private setHelptext(message = '', messages?: Map<string, string>) {
    this.ui.forms.setHelptext(new Map([[this.formName, message]]));
    this.ui.controls.setHelptext(messages);
  }

  private async signIn(googleUser: GoogleUser) {
    try {
      this.ui.masterControl.disabled = true;
      this.ui.masterControl.progress = true;
      this.setHelptext();

      const response = await this.snapAuth.signIn(AuthType.Google, {
        idToken: googleUser.getAuthResponse().id_token,
      });

      this.googleAuth.signOut();
      this.navigate(response);

    } catch (ex) {
      this.ui.masterControl.disabled = false;
      this.ui.masterControl.progress = false;

      if (this.snapAuth.isAuthException(ex)) {
        this.setHelptext(ex.message, ex.controls);

        if (ex.code === ErrorCode.AccountDoesNotExist) {
          this.signUpControl.hidden = false;
          this.signUpControl.focus();
        }

      } else {
        this.setHelptext(`Can't sign in`);
        const message = typeof ex === 'string' ? ex : ex.message;
        this.log.error(message);
        this.log.sendError(message);
      }
    }
  }

  private async signUp(googleUser: GoogleUser) {
    try {
      this.ui.masterControl.disabled = true;
      this.ui.masterControl.progress = true;
      this.setHelptext();

      const response = await this.snapAuth.signUp(AuthType.Google, {
        idToken: googleUser.getAuthResponse().id_token,
      });

      this.navigate(response);

    } catch (ex) {
      this.ui.masterControl.disabled = false;
      this.ui.masterControl.progress = false;

      if (this.snapAuth.isAuthException(ex)) {
        this.setHelptext(ex.message, ex.controls);

      } else {
        this.setHelptext(`Can't sign up`);
        const message = typeof ex === 'string' ? ex : ex.message;
        this.log.error(message);
        this.log.sendError(message);
      }
    }
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/google.component.ts